import { LexicalEditor as LexicalEditorType } from 'lexical'
import groupBy from 'lodash/groupBy'
import { AnimatePresence } from 'motion/react'
import { RefObject, useDeferredValue, useEffect, useRef, useState } from 'react'

import { AstraAccordion, AstraParagraph, AstraSelect, AstraSkeletonLoader, astraTwMerge } from '~/astra'
import { SidebarSelection } from '~/store/slices/selectionDrawerSlice'
import { useStore } from '~/store/store'
import { CommentsThread, LocationScheduleThread, useCommentThreadsBySelection, useLocations, useLocationScheduleThreadsByDate } from '~/storm'
import { getInitials } from '~/utils/user'
import { pluralize } from '~/utils/utils'

import { useCommentThreads } from '../RichTextEditing/useRichTextThread'
import { SearchText } from '../SearchText'
import { CommentThread, CommentThreadProps } from './CommentThread'
import { EmptyCommentList } from './EmptyCommentList'
import { LexicalEditor } from './lexical/LexicalEditor'
import { lexicalEditorToContentPayload } from './utils'

type StatusFilter = 'all' | 'hideResolved'

const SkeletonList = () =>
    Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="h-16 w-full">
            <AstraSkeletonLoader />
        </div>
    ))

const COMMENT_THREAD_WRAPPER_CLASS = 'comment-thread-wrapper'
const simpleMatch = (value: string, query: string) => value.toLowerCase().includes(query)

type ThreadListProps = {
    threads: CommentsThread[]
    search?: string
    statusFilter?: StatusFilter
    onReply: CommentThreadProps['onReply']
    onToggleReaction?: CommentThreadProps['onReaction']
    onResolveThread: CommentThreadProps['onResolveThread']
    onPinThread: CommentThreadProps['onPinThread']
    onDeleteThread: CommentThreadProps['onDeleteThread']
    onEditContent?: CommentThreadProps['onEditContent']
    listRef: RefObject<HTMLDivElement | null>
    showDivider?: boolean
}
const ThreadList = ({
    threads = [],
    search,
    onReply: onReply,
    onToggleReaction,
    onResolveThread,
    onPinThread,
    onDeleteThread,
    listRef,
    onEditContent,
    showDivider: divider = false,
}: ThreadListProps) => {
    const searchMatchThreads = search
        ? threads.filter(
              thread =>
                  simpleMatch(thread.content.raw_content, search) ||
                  simpleMatch(thread.updated_by ?? '', search) ||
                  thread.messages.some(message => simpleMatch(message.content.raw_content, search) || simpleMatch(message.updated_by ?? '', search))
          )
        : threads

    return (
        <AnimatePresence initial={false}>
            {searchMatchThreads.map(thread => (
                <CommentThread
                    key={thread.uuid}
                    className={`${COMMENT_THREAD_WRAPPER_CLASS}`}
                    thread={thread}
                    onReply={onReply}
                    rootPortalRef={listRef}
                    onReaction={onToggleReaction}
                    onResolveThread={onResolveThread}
                    onPinThread={onPinThread}
                    onDeleteThread={onDeleteThread}
                    onEditContent={onEditContent}
                />
            ))}
            {divider && searchMatchThreads.length > 0 && <span className="my-1 grow border border-subtle" />}
        </AnimatePresence>
    )
}

const GroupedLocationThreads = ({ threads, ...threadListProps }: { threads: LocationScheduleThread[] } & Omit<ThreadListProps, 'threads'>) => {
    const [showLocationThreads, setShowLocationThreads] = useState(true)
    const locationIds = [...new Set(threads.map(thread => thread.ref.location_id))]
    const locationById = useLocations({ where: { id: { in: locationIds } }, keyBy: 'id' })

    const threadsGroupedByLocation = Object.values(groupBy(threads, 'ref.location_id')).map(threads => {
        const locId = threads[0]?.ref.location_id
        const pinnedThreads: LocationScheduleThread[] = []
        const notPinned = threads.filter(thread => {
            if (thread.pinned) {
                pinnedThreads.push(thread)
                return false
            }
            return true
        })
        return {
            locationId: locId,
            threads: notPinned,
            pinnedThreads: pinnedThreads,
        }
    })

    if (threadsGroupedByLocation.length === 0) return null

    const totalThreads = threadsGroupedByLocation.reduce((acc, group) => acc + group.threads.length + group.pinnedThreads.length, 0)
    const threadsForLocationItems = threadsGroupedByLocation
        .filter((group): group is { locationId: number; threads: typeof threads; pinnedThreads: typeof threads } => group.locationId !== undefined)
        .map(({ locationId, threads, pinnedThreads }) => ({
            value: locationId.toString(),
            title: locationById.get(locationId)?.room_code ?? '',
            content: (
                <div className="flex flex-col gap-3">
                    <ThreadList {...threadListProps} threads={pinnedThreads} showDivider={threads.length > 0} />
                    <ThreadList {...threadListProps} threads={threads} />
                </div>
            ),
        }))

    return (
        <div className="flex flex-col gap-1">
            <button className="flex cursor-pointer items-center gap-2 text-subtle" onClick={() => setShowLocationThreads(prev => !prev)}>
                <span className="grow border border-subtle" />
                <AstraParagraph className="text-subtle">
                    {showLocationThreads
                        ? `${totalThreads} ${pluralize(totalThreads, 'kommentar')} på operasjonsrom`
                        : `Vis ${totalThreads} ${pluralize(totalThreads, 'kommentar')} på operasjonsrom`}
                </AstraParagraph>
                <span className="grow border border-subtle" />
            </button>

            <div className="px-2">
                {showLocationThreads && (
                    <AstraAccordion itemSeparator="gap" defaultOpen={threadsForLocationItems.map(item => item.value)} items={threadsForLocationItems} />
                )}
            </div>
        </div>
    )
}

export const CommentList = ({
    selection,
    showFilters = true,
    showEmpty = true,
    autoFocusInput = false,
    showGroupedLocations = true,
}: {
    selection: SidebarSelection
    showFilters?: boolean
    showEmpty?: boolean
    autoFocusInput?: boolean
    showGroupedLocations?: boolean
}) => {
    const {
        isLoading: isLoadingThreads,
        createThreadMutation,
        createMessageMutation,
        toggleReaction,
        toggleResolveThread,
        togglePinThread,
        archiveThread,
        editContent,
    } = useCommentThreads({ selection })
    const [searchText, setSearchText] = useState('')

    const [statusFilter, setStatusFilter] = useState<StatusFilter>('hideResolved')
    const user = useStore(state => state.user)
    const userInitials = getInitials(user.display_name ?? '')

    const deferredSearch = useDeferredValue(searchText)

    const inputRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    const filterThreadsOptions = { resolved: statusFilter === 'all' ? 'include' : false } as const

    const threadsForSelection = useCommentThreadsBySelection(selection, { ...filterThreadsOptions, pinned: 'include' })
    const pinnedThreads = threadsForSelection.filter(thread => thread.pinned)
    const threads = threadsForSelection.filter(thread => !thread.pinned)
    const locationThreadsForDate = useLocationScheduleThreadsByDate(
        selection.type === 'scheduleDate' ? selection.date : undefined,
        selection.type === 'scheduleDate' ? { ...filterThreadsOptions, pinned: 'include' } : undefined
    )
    const isEmpty = !isLoadingThreads && threads.length === 0 && locationThreadsForDate.length === 0 && pinnedThreads.length === 0

    const scrollBasedOnFocusedElement = () => {
        const activeElement = document.activeElement
        // activeElement is the main-input - eg. we added a new thread, scroll to bottom
        if (activeElement === inputRef.current) {
            setTimeout(() => {
                listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
            }, 0)
        }
        // if it's not the main-input but is lexical-editor - its a reply
        // keep the reply-input in view
        else if (activeElement?.hasAttribute('data-lexical-editor')) {
            // try to find the closest comment-thread-wrapper, so we can cleanly scroll to the bottom of that container
            // (without hacking in magic numbers for the wrapper height etc.)
            // if not, just fallback to scroll to the active element
            const threadContainer = activeElement.closest(`.${COMMENT_THREAD_WRAPPER_CLASS}`)
            if (threadContainer) {
                const rect = threadContainer.getBoundingClientRect()
                const boundingRect = listRef.current?.getBoundingClientRect()
                const boundingY = (boundingRect?.top ?? 0) + (boundingRect?.height ?? 0)
                if (rect.top + rect.height > boundingY) {
                    threadContainer.scrollIntoView({ behavior: 'smooth', block: 'end' })
                    return
                }
            }
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }

    // Scroll to bottom when new comment is added
    useEffect(scrollBasedOnFocusedElement, [threads])

    const handleSendMessage = async (editor: LexicalEditorType) => {
        await createThreadMutation.mutateAsync(lexicalEditorToContentPayload(editor))
    }

    const threadListProps: ThreadListProps = {
        search: deferredSearch,
        statusFilter,
        onReply: async (thread, content) => {
            await createMessageMutation.mutateAsync({ ...content, threadId: thread.id })
        },
        onEditContent: async (thread, { messageId, raw_content, mentions }) => {
            await editContent({ thread, messageId, raw_content, mentions })
        },
        listRef,
        onToggleReaction: toggleReaction,
        onResolveThread: toggleResolveThread,
        onPinThread: togglePinThread,
        onDeleteThread: archiveThread,
        threads,
    }

    return (
        <div className="z-[0] flex h-full flex-col overflow-auto">
            {showFilters && (
                <div className="z-elevated flex justify-between px-4 py-3 shadow-sm">
                    <SearchText
                        rawNeedle={searchText}
                        setNeedle={value => {
                            setSearchText(value)
                        }}
                    />
                    <AstraSelect<StatusFilter>
                        options={[
                            { label: 'Alle', value: 'all' },
                            { label: 'Skjul løste', value: 'hideResolved' },
                        ]}
                        value={statusFilter}
                        onChange={value => {
                            if (!value) return
                            setStatusFilter(value)
                        }}
                    />
                </div>
            )}
            <div className={astraTwMerge('flex flex-col gap-3 overflow-auto', isEmpty ? 'pb-1' : 'pb-4')}>
                <div className="flex w-full overflow-auto scroll-smooth px-4" ref={listRef}>
                    <div className="relative flex h-max w-full flex-col gap-3 overflow-hidden pt-3">
                        {isLoadingThreads && <SkeletonList />}

                        {pinnedThreads.length > 0 && <ThreadList {...threadListProps} threads={pinnedThreads} showDivider />}
                        <ThreadList {...threadListProps} />

                        {selection.type === 'scheduleDate' && showGroupedLocations && (
                            <GroupedLocationThreads {...threadListProps} threads={locationThreadsForDate} />
                        )}
                    </div>
                </div>
            </div>
            {showEmpty && isEmpty && (
                <div className="mb-auto mt-auto flex w-full items-center">
                    <EmptyCommentList />
                </div>
            )}
            <div className="relative mt-auto px-4 pb-4">
                <LexicalEditor onSendMessage={handleSendMessage} key="input" userInitials={userInitials} inputRef={inputRef} autoFocus={autoFocusInput} />
            </div>
        </div>
    )
}
